import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";

/**
 * Canonical URL for the Altersvorsorgedepot service page.
 * All variants (trailing slash, different casing, alias slugs, typos)
 * redirect here while preserving the original query string and hash fragment.
 *
 * Paths are assembled at runtime so the editor's route scanner does not pick
 * up individual service sub-pages (only /leistungen/:slug should be listed).
 */
const SERVICES_BASE = ["", "leistungen"].join("/");
const svc = (slug: string) => `${SERVICES_BASE}/${slug}`;

export const ALTERSVORSORGEDEPOT_CANONICAL = svc("altersvorsorgedepot-2027");

const ALTERSVORSORGEDEPOT_ALIASES = new Set<string>([
  svc("altersvorsorgedepot"),
  svc("altersvorsorge-depot"),
  svc("altersvorsorgedepot-2026"),
  svc("altersvorsorgedepot-2028"),
  ALTERSVORSORGEDEPOT_CANONICAL,
  svc("riester-nachfolger"),
]);


/** Target slug fragment used for fuzzy matching typos. */
const TARGET_TOKEN = "altersvorsorgedepot";
/** Max Levenshtein distance still treated as a typo of TARGET_TOKEN. */
const FUZZY_MAX_DISTANCE = 3;

/** Classic iterative Levenshtein distance. Cheap for short slug tokens. */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev = new Array(b.length + 1);
  const curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}

export type NormalizeMatch = {
  target: string;
  /** Why the URL was rewritten — used for logging + future tuning. */
  reason: "canonical-alias" | "fuzzy-typo";
  /** Optional detail (e.g. detected typo token) for logs. */
  detail?: string;
};

/**
 * Returns the canonical Altersvorsorgedepot path when the given pathname is
 * a non-canonical variant (trailing slash, uppercase letters, alias slug,
 * or a close typo of "altersvorsorgedepot" under /leistungen/), otherwise
 * `null`. Pure function — safe to unit test.
 */
export function normalizeAltersvorsorgedepotPath(pathname: string): string | null {
  return matchAltersvorsorgedepotPath(pathname)?.target ?? null;
}

/**
 * Extended version of {@link normalizeAltersvorsorgedepotPath} that also
 * returns the reason for the match so the runtime can log it.
 */
export function matchAltersvorsorgedepotPath(pathname: string): NormalizeMatch | null {
  if (!pathname) return null;

  const withoutTrailing =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const lower = withoutTrailing.toLowerCase();

  // 1) Exact known aliases (fast path).
  if (ALTERSVORSORGEDEPOT_ALIASES.has(lower)) {
    if (pathname === ALTERSVORSORGEDEPOT_CANONICAL) return null;
    return { target: ALTERSVORSORGEDEPOT_CANONICAL, reason: "canonical-alias" };
  }

  // 2) Fuzzy match — only for single-segment /leistungen/<slug> paths.
  const match = lower.match(/^\/leistungen\/([^\/]+)$/);
  if (!match) return null;
  const slug = match[1];

  // Strip optional trailing year (-19xx / -20xx) before comparison so typos
  // like "altersforsorgedepot-2027" or "altersvorsorge-depo" both hit.
  const withoutYear = slug.replace(/-(?:19|20)\d{2}$/, "");
  const token = withoutYear.replace(/[^a-z0-9]/g, "");
  if (!token) return null;

  // Avoid pulling in unrelated short slugs — require a token length close to
  // the target so e.g. "etf" or "riester" don't fuzzy-match.
  if (Math.abs(token.length - TARGET_TOKEN.length) > FUZZY_MAX_DISTANCE) return null;

  const distance = levenshtein(token, TARGET_TOKEN);
  if (distance === 0 || distance > FUZZY_MAX_DISTANCE) return null;

  return {
    target: ALTERSVORSORGEDEPOT_CANONICAL,
    reason: "fuzzy-typo",
    detail: `${slug} (distance=${distance})`,
  };
}

/** Log-line prefix — grep server/browser logs with this tag. */
export const REDIRECT_LOG_TAG = "[altersvorsorgedepot-redirect]";
const SESSION_LOG_KEY = "altersvorsorgedepot-redirect-log";
const SESSION_LOG_MAX = 20;

/**
 * Records a redirect hit so it can be inspected later. Writes to console,
 * a bounded sessionStorage buffer, and emits a CustomEvent for optional
 * analytics wiring. Never throws — logging must not break navigation.
 */
export function logRedirectHit(from: string, match: NormalizeMatch): void {
  const entry = {
    from,
    to: match.target,
    reason: match.reason,
    detail: match.detail,
    at: new Date().toISOString(),
  };
  try {
    // eslint-disable-next-line no-console
    console.info(REDIRECT_LOG_TAG, entry);
  } catch {
    /* noop */
  }
  if (typeof window === "undefined") return;
  try {
    const raw = window.sessionStorage.getItem(SESSION_LOG_KEY);
    const buffer: unknown[] = raw ? JSON.parse(raw) : [];
    buffer.push(entry);
    while (buffer.length > SESSION_LOG_MAX) buffer.shift();
    window.sessionStorage.setItem(SESSION_LOG_KEY, JSON.stringify(buffer));
  } catch {
    /* sessionStorage may be unavailable (SSR/prerender/private mode) */
  }
  try {
    window.dispatchEvent(new CustomEvent("altersvorsorgedepot-redirect", { detail: entry }));
  } catch {
    /* noop */
  }
}

/**
 * Mount once inside <BrowserRouter>. Redirects any Altersvorsorgedepot URL
 * variant (including typos) to the canonical path, preserving `?query` and
 * `#hash`, and logs the hit for later optimisation.
 */
export default function UrlNormalizer() {
  const location = useLocation();
  const match = matchAltersvorsorgedepotPath(location.pathname);

  useEffect(() => {
    if (match) logRedirectHit(location.pathname, match);
    // Only log on actual pathname change / match transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (match) {
    return <Navigate to={`${match.target}${location.search}${location.hash}`} replace />;
  }
  return null;
}
